/**
 * Lenavio's natural-language command bar.
 *
 * Two surfaces in one component:
 *
 *   1. Inline — embedded under the dashboard greeting card. No
 *      modal chrome, just the input + response area below it.
 *   2. Modal — opened from anywhere via Cmd+K / Ctrl+K. Same
 *      input + response area, rendered inside a center-screen
 *      overlay. The provider in App.tsx controls open state.
 *
 * Pipeline per submission:
 *   POST /ai/command  →  CommandResponse
 *     - type=navigate → react-router-dom navigate(route)
 *     - type=action with requires_confirmation → render Confirm/
 *       Cancel card; on Confirm, POST again with confirmed=true
 *     - type=report → render inline bar chart from
 *       visualization.series
 *     - type=query / agent / denied / error / unknown → render
 *       text card
 *   After every response, render suggestions as chips that re-fire
 *   the command on click.
 *
 * Persists the last 10 commands per browser via localStorage so the
 * empty-state shows a "Recent" list. Conversation context is held
 * server-side (last 3 exchanges per conversation_id); we just send
 * the same id back for follow-ups.
 */
import {
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Check,
  ClipboardList,
  Download,
  History,
  Search,
  ShieldOff,
  Sparkles,
  TriangleAlert,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import {
  scheduleCommand,
  streamCommand,
  type CommandResponse,
  type CommandStreamEvent,
} from "@/api/aiCommand";
import { ReportRenderer } from "@/components/command/ReportRenderer";
import { MarkdownText } from "@/components/command/MarkdownText";
import { CommandBarFirstUseTooltip } from "@/components/command/CommandBarFirstUseTooltip";
// ``reportPdf`` pulls in jspdf + jspdf-autotable + html2canvas
// (~430KB minified). Only loaded when the user clicks "Download PDF"
// in the report action row — see the dynamic ``await import`` below.
// Keeping it out of the initial bundle saves every user that cold
// half a megabyte of JS they never use.
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";

const HISTORY_KEY = "lenavio.command.history";
const MAX_HISTORY = 10;

/** First-time discovery prompts, keyed by role. Each role sees only
 *  the chips that map to capabilities it actually has — showing an
 *  employee "run sentinel" or "flight risks" creates a misleading
 *  expectation and a guaranteed denial when they click it. The
 *  backend's ``check_role_escalation`` enforces this server-side;
 *  these chips just keep the UI honest about what's reachable. */
const STARTER_PROMPTS_BY_ROLE: Record<string, string[]> = {
  EMPLOYEE: ["my PTO", "my documents", "directory"],
  MANAGER: ["my team", "pending approvals", "team PTO"],
  HR_ADMIN: ["headcount report", "approve leave", "flight risks", "run sentinel"],
  SUPER_ADMIN: ["headcount report", "approve leave", "flight risks", "run sentinel"],
};

/** Rotating placeholders for the input, keyed by role. Cycles every
 *  4s so the user sees the breadth of what they can ask without
 *  committing screen real estate to a tour. Each role gets prompts
 *  that match its actual capability surface — employees see self-
 *  service queries, managers see team-scoped questions, admins see
 *  the full org-wide command set.
 *
 *  Rationale for separating the lists rather than filtering one
 *  master list: the wording for a manager prompt is naturally
 *  different from an admin prompt ("Show me my team" vs "Show me
 *  headcount by department" — same shape, different scope). A
 *  shared list with generic phrasing would feel less crafted; the
 *  per-role lists let each prompt read like it was written for
 *  that user's job. */
const ROTATING_PLACEHOLDERS_BY_ROLE: Record<string, string[]> = {
  EMPLOYEE: [
    "How much PTO do I have left?",
    "Find someone in the directory",
    "What documents need my signature?",
    "Show my training courses",
    "Who's on my team?",
  ],
  MANAGER: [
    "Show me my team",
    "Any pending leave requests?",
    "How much PTO does my team have?",
    "Approve all leave requests",
    "Show team headcount",
    "Who on my team is up for review?",
  ],
  HR_ADMIN: [
    "What are we doing today?",
    "Show me headcount by department",
    "Approve all pending leave requests",
    "Who hasn't completed compliance training?",
    "Generate a turnover report for Q1",
    "How much PTO does Engineering have left?",
    "Run a compensation benchmark for Sales",
    "What did Sentinel find this morning?",
    "Show me this week's Atlas briefing",
    "Who are my flight risks right now?",
    "Create an expense summary for April",
    "Which employees are in their first 90 days?",
    "How many open positions do we have?",
    "Show me benefits enrollment status",
    "Draft a job description for Product Manager",
  ],
  SUPER_ADMIN: [
    "What are we doing today?",
    "Show me headcount by department",
    "Approve all pending leave requests",
    "Who hasn't completed compliance training?",
    "Generate a turnover report for Q1",
    "How much PTO does Engineering have left?",
    "Run a compensation benchmark for Sales",
    "What did Sentinel find this morning?",
    "Show me this week's Atlas briefing",
    "Who are my flight risks right now?",
    "Create an expense summary for April",
    "Which employees are in their first 90 days?",
    "How many open positions do we have?",
    "Show me benefits enrollment status",
    "Draft a job description for Product Manager",
  ],
};

/** Fallback list used when the user isn't authenticated yet or the
 *  role isn't one of the four known values. Defaults to the most
 *  restrictive set (EMPLOYEE) so we never accidentally surface admin
 *  prompts to a user that doesn't have admin role. */
function placeholdersForRole(role: string | undefined): string[] {
  if (!role) return ROTATING_PLACEHOLDERS_BY_ROLE.EMPLOYEE;
  return (
    ROTATING_PLACEHOLDERS_BY_ROLE[role] ??
    ROTATING_PLACEHOLDERS_BY_ROLE.EMPLOYEE
  );
}

function starterPromptsForRole(role: string | undefined): string[] {
  if (!role) return STARTER_PROMPTS_BY_ROLE.EMPLOYEE;
  return STARTER_PROMPTS_BY_ROLE[role] ?? STARTER_PROMPTS_BY_ROLE.EMPLOYEE;
}

interface CommandBarProps {
  /** When false, the input is rendered without the inline placeholder
   *  shadow — used by the modal which adds its own framing. */
  variant?: "inline" | "modal";
  /** The modal calls this when the user closes via Esc / overlay. */
  onClose?: () => void;
  /** Used to autofocus on mount in the modal. */
  autoFocus?: boolean;
  className?: string;
}

type TurnStatus = "streaming" | "done" | "error" | "cancelled";

/** One exchange in the chat thread — the user's message, the live
 *  progress events as they arrive, and the final ``CommandResponse``
 *  once the stream resolves. ``status`` drives which surface
 *  renders for this turn (the streaming card vs the final
 *  ``ResponseCard`` + sources panel). */
interface ThreadTurn {
  id: string;
  userMessage: string;
  progress: CommandStreamEvent[];
  response: CommandResponse | null;
  status: TurnStatus;
}

function newTurnId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `turn-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function readHistory(): string[] {
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
}

function writeHistory(next: string[]): void {
  try {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next.slice(0, MAX_HISTORY)));
  } catch {
    /* localStorage may be disabled — ignore */
  }
}

function pushHistory(command: string): string[] {
  const trimmed = command.trim();
  if (!trimmed) return readHistory();
  const existing = readHistory().filter(
    (c) => c.toLowerCase() !== trimmed.toLowerCase(),
  );
  const next = [trimmed, ...existing].slice(0, MAX_HISTORY);
  writeHistory(next);
  return next;
}

/** Stable per-tab conversation id so server-side context survives
 *  multiple commands within a session. */
function newConversationId(): string {
  // crypto.randomUUID is available everywhere we ship.
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `convo-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function CommandBar({
  variant = "inline",
  onClose,
  autoFocus = false,
  className,
}: CommandBarProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  // Role drives placeholder rotation, starter chips, and (server-
  // side) the planner's system prompt / tool catalog / Skills gate.
  // We default to EMPLOYEE when no user is loaded so we never
  // accidentally show admin-shaped prompts to a guest/loading state.
  const { user } = useAuth();
  const userRole = user?.role ?? "EMPLOYEE";
  const placeholders = placeholdersForRole(userRole);
  const starterPrompts = starterPromptsForRole(userRole);
  // Per-turn AbortController for the in-flight stream. Stored in a
  // ref because the Stop button needs to grab the latest one even
  // mid-render; only one turn can be streaming at a time.
  const abortRef = useRef<AbortController | null>(null);
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  // The Sana-style chat thread — every turn stays visible above the
  // input. ``response`` is null until the final stream event lands;
  // ``progress`` accumulates the live tool-call telemetry; ``status``
  // tracks the lifecycle so we can render a different surface per
  // state (streaming → done → cancelled / error).
  const [thread, setThread] = useState<ThreadTurn[]>([]);
  // ``confirming`` lives at thread level — one turn might be in the
  // confirmation handshake while older turns are settled. We key it
  // by turn id so the right Confirm button appears under the right
  // turn.
  const [confirmingTurnId, setConfirmingTurnId] = useState<string | null>(null);
  const [showRecents, setShowRecents] = useState(false);
  const [recents, setRecents] = useState<string[]>(() => readHistory());
  // Rotating placeholder index — advances every 4s while the input
  // is empty + idle. Pauses when the user is typing or a turn is
  // visible so the cursor area stays stable.
  const [phIdx, setPhIdx] = useState(0);
  const [phFade, setPhFade] = useState(true);
  useEffect(() => {
    if (loading || value.trim() || thread.length > 0) return;
    const fadeInterval = setInterval(() => {
      setPhFade(false);
      window.setTimeout(() => {
        setPhIdx((i) => (i + 1) % placeholders.length);
        setPhFade(true);
      }, 200);
    }, 4000);
    return () => clearInterval(fadeInterval);
  }, [loading, value, thread.length]);

  // Auto-scroll the thread to the bottom whenever a new turn lands
  // or progress updates fire. ``smooth`` for the natural feel of a
  // streaming chat.
  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [thread]);

  // Stable conversation id per mount of the bar — same convo across
  // many commands until the user reloads, clears, or closes the modal.
  const conversationIdRef = useRef<string>(newConversationId());

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Helper: patch a turn in-place by id without losing earlier ones.
  const updateTurn = useCallback(
    (turnId: string, patch: Partial<ThreadTurn>) => {
      setThread((prev) =>
        prev.map((t) => (t.id === turnId ? { ...t, ...patch } : t)),
      );
    },
    [],
  );

  const submit = useCallback(
    async (
      raw: string,
      opts?: { confirmed?: boolean; replaceTurnId?: string },
    ) => {
      const command = raw.trim();
      if (!command) return;
      setLoading(true);
      setShowRecents(false);

      // Append a new pending turn unless we're replacing (e.g. on
      // confirm — the original turn stays in the thread, the new
      // one rides under it).
      const turnId = newTurnId();
      setThread((prev) => [
        ...prev,
        {
          id: turnId,
          userMessage: command,
          progress: [],
          response: null,
          status: "streaming",
        },
      ]);

      // Wire an AbortController so the Stop button can cancel mid-
      // stream. Stash it where the click handler can find it.
      const ac = new AbortController();
      abortRef.current = ac;

      try {
        let resp: CommandResponse;
        try {
          resp = await streamCommand(
            {
              command,
              conversation_id: conversationIdRef.current,
              confirmed: !!opts?.confirmed,
            },
            (ev) => {
              // Append progress to THIS turn only. Older turns'
              // progress trails are frozen at their final state.
              setThread((prev) =>
                prev.map((t) =>
                  t.id === turnId
                    ? { ...t, progress: [...t.progress, ev] }
                    : t,
                ),
              );
            },
            ac.signal,
          );
        } catch (streamErr) {
          // ``AbortError`` from a Stop click — mark cancelled and
          // bail. NOT an error — the user asked to stop.
          if (
            streamErr instanceof DOMException &&
            streamErr.name === "AbortError"
          ) {
            updateTurn(turnId, { status: "cancelled" });
            return;
          }
          // NO silent fallback to runCommand. Hiding stream failures
          // behind the non-streaming endpoint means real errors
          // (engine crashes, missing migrations, bad prompts, etc.)
          // never surface — we fix symptoms instead of root causes.
          // Surface the failure as an error turn so the user sees
          // exactly what broke.
          console.error("streamCommand failed:", streamErr);
          const message =
            streamErr instanceof Error
              ? streamErr.message
              : String(streamErr);
          updateTurn(turnId, {
            status: "error",
            response: {
              type: "error",
              text: `Stream failed: ${message}`,
              sections: [],
              actions: [],
              suggestions: [],
              route: null,
              requires_confirmation: false,
              conversation_id: conversationIdRef.current,
            } satisfies CommandResponse,
          });
          return;
        }

        // ── Defensive nav guard ─────────────────────────────────
        // If the response carries inline content (sections, a real
        // text body, or a confirmation handshake), the user just
        // got an answer in the chat — navigating away would yank
        // it from under them. We only honor ``route`` when the
        // payload is purely a navigation directive.
        const hasInlineAnswer =
          (resp.sections && resp.sections.length > 0) ||
          resp.requires_confirmation ||
          (resp.type === "report" || resp.type === "query") ||
          ((resp.text || "").trim().length > 40);

        // Side-effects first.
        if (resp.type === "navigate" && resp.route && !hasInlineAnswer) {
          setRecents(pushHistory(command));
          if (variant === "modal" && onClose) onClose();
          navigate(resp.route);
          return;
        }

        // Confirmation path — keep the turn in the thread, mark it
        // as awaiting confirmation. Two distinct flows land here:
        //   * ``requires_confirmation`` — the legacy "are you sure
        //     you want to do this mutation?" handshake (delete Bob,
        //     send announcement, etc.).
        //   * ``requires_credit_confirmation`` — the new "this will
        //     cost N credits, you can also do it from the UI"
        //     pause for Tier-3 commands.
        // Both re-issue with ``confirmed: true`` when the user
        // clicks Confirm; the engine handles them with the same
        // ``confirmed`` flag.
        if (resp.requires_confirmation || resp.requires_credit_confirmation) {
          updateTurn(turnId, { response: resp, status: "done" });
          setConfirmingTurnId(turnId);
        } else {
          updateTurn(turnId, { response: resp, status: "done" });
          if (confirmingTurnId === turnId) setConfirmingTurnId(null);
          // Follow-up route navigation (e.g. employees.create pre-fill)
          // — but ONLY when there's nothing inline to read. Otherwise
          // the inline chart/table/stats would vanish behind a redirect.
          if (resp.route && !hasInlineAnswer) {
            setTimeout(() => {
              if (variant === "modal" && onClose) onClose();
              if (resp.route) navigate(resp.route);
            }, 250);
          }
        }
        setRecents(pushHistory(command));
        setValue("");
      } catch (err) {
        updateTurn(turnId, {
          status: "error",
          response: {
            type: "error",
            text:
              err instanceof Error
                ? err.message
                : "Something went wrong. Please try again.",
            data: null,
            visualization: null,
            route: null,
            requires_confirmation: false,
            suggestions: [],
            intent: null,
            conversation_id: conversationIdRef.current,
          },
        });
      } finally {
        setLoading(false);
        abortRef.current = null;
      }
    },
    [confirmingTurnId, navigate, onClose, updateTurn, variant],
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!loading) submit(value);
  };

  // Confirm: re-submit the same prompt with confirmed=true. The
  // original "preview" turn stays in the thread; the executed result
  // appears as the next turn.
  const handleConfirm = (turnId: string) => {
    const turn = thread.find((t) => t.id === turnId);
    if (!turn) return;
    setConfirmingTurnId(null);
    submit(turn.userMessage, { confirmed: true });
  };

  const handleCancelConfirm = (turnId: string) => {
    if (confirmingTurnId === turnId) setConfirmingTurnId(null);
    // Keep the turn visible — just drop the confirmation pending
    // state. The user can still scroll back and read what we
    // proposed without it being actionable any more.
  };

  const handleStop = () => {
    abortRef.current?.abort();
    abortRef.current = null;
  };

  const handleClearConversation = () => {
    handleStop(); // Cancel any in-flight stream first.
    setThread([]);
    setConfirmingTurnId(null);
    // Fresh conversation_id so the server-side memory doesn't blend
    // the cleared prior context into the next turn.
    conversationIdRef.current = newConversationId();
    setLoading(false);
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      if (variant === "modal" && onClose) onClose();
      setShowRecents(false);
    }
  };

  return (
    <div className={className}>
      <form onSubmit={handleSubmit} className="relative">
        {/* Always-on brand-blue glow behind the bar. This is the
            single visual cue that the CommandBar is THE primary
            surface on the page — outshines the topbar's search pill
            (which is a smaller pre-styled pill) and the dashboard
            cards. Brightens on focus + while loading. The base
            opacity is intentionally non-zero so even an idle bar
            reads as "live". */}
        <div
          className={
            "absolute -inset-1 rounded-bento bg-gradient-to-r " +
            "from-accent/30 via-accent/15 to-accent/30 " +
            "transition-opacity duration-500 blur-xl " +
            (loading
              ? "opacity-80"
              : "opacity-30 group-focus-within:opacity-70")
          }
          aria-hidden
        />
        <div
          className={
            "group relative flex items-center gap-3 rounded-bento " +
            "border border-accent/20 bg-surface px-5 py-4 " +
            "shadow-bento transition-all duration-300 " +
            "focus-within:border-accent focus-within:shadow-[0_8px_30px_rgba(27,24,255,0.15)] " +
            "hover:border-accent/40 " +
            (variant === "inline" ? "min-h-[64px]" : "")
          }
        >
          <span className="text-accent shrink-0">
            {loading ? (
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, 8, -8, 0],
                  opacity: [0.7, 1, 0.7],
                }}
                transition={{ duration: 1.2, repeat: Infinity }}
              >
                <Sparkles className="h-7 w-7 drop-shadow-[0_0_8px_rgba(27,24,255,0.5)]" />
              </motion.div>
            ) : (
              <motion.div
                animate={{
                  rotate: [0, 4, -4, 0],
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Sparkles className="h-7 w-7 drop-shadow-[0_0_6px_rgba(27,24,255,0.35)]" />
              </motion.div>
            )}
          </span>
          <input
            ref={inputRef}
            type="text"
            value={value}
            disabled={loading}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setShowRecents(value.trim().length === 0)}
            onBlur={() => {
              // Delay so a click on a recent chip lands first.
              setTimeout(() => setShowRecents(false), 150);
            }}
            onKeyDown={onKey}
            placeholder={
              loading
                ? "Thinking…"
                : placeholders[phIdx % placeholders.length]
            }
            className={
              "flex-1 bg-transparent text-[15px] font-medium text-text-primary " +
              "focus:outline-none disabled:opacity-60 transition-opacity duration-200 " +
              (phFade ? "placeholder:opacity-100" : "placeholder:opacity-60") +
              " placeholder-text-muted/80"
            }
          />
          {variant === "modal" && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-text-muted hover:text-text-primary p-1.5 rounded-lg hover:bg-surface-muted"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {variant === "inline" && (
            <span className="hidden sm:inline-flex items-center gap-0.5 text-[11px] font-bold tracking-wide text-accent px-2 py-1 rounded-md border border-accent/30 bg-accent/5 font-mono">
              <kbd>⌘</kbd>
              <kbd>K</kbd>
            </span>
          )}
        </div>

        {/* Recent commands — shows when input is focused + empty. */}
        <AnimatePresence>
          {showRecents && recents.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="absolute left-0 right-0 mt-2 z-20 rounded-bentoSm border border-border bg-surface shadow-bentoHover p-2"
            >
              <div className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-text-muted">
                <History className="h-3.5 w-3.5" />
                Recent
              </div>
              <ul>
                {recents.map((r) => (
                  <li key={r}>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => submit(r)}
                      className="w-full text-left text-sm text-text-primary hover:bg-accent/5 hover:text-accent px-2.5 py-2 rounded-lg transition-colors"
                    >
                      {r}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </form>

      {/* First-use tooltip — fires once per browser to set the
          framing on day one: "this is a shortcut, not a
          requirement." Inline variant only; the modal variant is
          opt-in (user explicitly opened the palette) so no
          tutorial needed there. */}
      {variant === "inline" && thread.length === 0 && (
        <CommandBarFirstUseTooltip />
      )}

      {/* Starter prompts — only on the inline variant, only when the
          user hasn't typed yet, hasn't run a command yet, and we
          aren't loading. Shown so the bar's intent is unmistakable:
          this is The Way to interact with the platform, not a
          chatbot in the corner. */}
      {variant === "inline" &&
        !loading &&
        thread.length === 0 &&
        value.trim().length === 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 px-1">
            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-text-muted">
              Try
            </span>
            {starterPrompts.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => submit(p)}
                className="text-xs font-medium px-3 py-1.5 rounded-full border border-border bg-surface text-text-primary hover:bg-accent/5 hover:border-accent/40 hover:text-accent transition-all duration-150 active:scale-[0.97]"
              >
                {p}
              </button>
            ))}
          </div>
        )}

      {/* ── Chat thread ──────────────────────────────────────────
          Sana-style: every turn stays visible above the dashboard
          (or in the modal). User bubbles right-aligned, AI cards
          left-aligned, sources cited below each answer. Empty
          until the user submits the first turn — keeps the
          dashboard clean on cold start.

          Inline variant: cap visible height at ~70vh so a long
          conversation doesn't push the rest of the dashboard
          off-screen. Modal variant doesn't need a cap — the modal
          shell already scrolls.
      */}
      {thread.length > 0 && (
        <div
          className={cn(
            "mt-3",
            variant === "inline" && "max-h-[70vh] overflow-y-auto pr-1",
          )}
        >
          {/* Thread header: turn count + Clear button + Stop */}
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-text-muted">
                Conversation
              </span>
              <span className="text-[11px] tabular-nums text-text-muted">
                {thread.length} turn{thread.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {loading && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleStop}
                  className="text-warning hover:text-warning"
                >
                  <X className="h-3.5 w-3.5" />
                  Stop
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClearConversation}
                disabled={loading && thread.length === 1}
              >
                Clear
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            {thread.map((turn) => (
              <TurnCard
                key={turn.id}
                turn={turn}
                isConfirming={confirmingTurnId === turn.id}
                onConfirm={() => handleConfirm(turn.id)}
                onCancelConfirm={() => handleCancelConfirm(turn.id)}
                onSuggestion={(s) => submit(s)}
              />
            ))}
            <div ref={threadEndRef} />
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// TurnCard — one turn in the thread (user message + agent reply)
// ════════════════════════════════════════════════════════════════════

function TurnCard({
  turn,
  isConfirming,
  onConfirm,
  onCancelConfirm,
  onSuggestion,
}: {
  turn: ThreadTurn;
  isConfirming: boolean;
  onConfirm: () => void;
  onCancelConfirm: () => void;
  onSuggestion: (s: string) => void;
}) {
  return (
    <div className="space-y-2">
      {/* User bubble — right-aligned indigo. */}
      <div className="flex justify-end">
        <div
          className="max-w-[80%] rounded-2xl rounded-br-sm px-4 py-2 text-sm text-white shadow-sm"
          style={{
            background: "linear-gradient(135deg, #1B18FF, #4340FF)",
          }}
        >
          {turn.userMessage}
        </div>
      </div>

      {/* Streaming state. Once the model starts emitting draft text
          (synth ``text_delta`` events), show it building live. Before
          that — during planning / tool calls — show the progress trail,
          or the thinking card if no events have arrived yet. */}
      {turn.status === "streaming" && !turn.response && (
        (() => {
          const draft = turn.progress.reduce(
            (acc, ev) =>
              ev.kind === "synth" && ev.text_delta ? acc + ev.text_delta : acc,
            "",
          );
          if (draft) return <StreamingDraftCard text={draft} />;
          return turn.progress.length > 0 ? (
            <ProgressTrail events={turn.progress} />
          ) : (
            <ThinkingCard />
          );
        })()
      )}

      {/* Cancelled — subtle pill instead of a full card. */}
      {turn.status === "cancelled" && (
        <div className="text-xs text-text-muted italic px-1">
          ✕ Cancelled by you
        </div>
      )}

      {/* Final response. */}
      {turn.response && (
        <ResponseCard
          response={turn.response}
          confirming={isConfirming}
          onConfirm={onConfirm}
          onCancelConfirm={onCancelConfirm}
          onSuggestion={onSuggestion}
        />
      )}

      {/* Sources / tool-call panel — internal diagnostic only.
          End-users see this as noise ("Sources · 2 tools" is debug
          chatter, not insight). We keep the component mounted but
          gate it behind a query-string flag so support / engineers
          can still inspect tool traces in prod by appending
          ``?debug=tools`` to the URL. The default view is clean. */}
      {showSourcesDebug() &&
        turn.response?.debug?.log &&
        turn.response.debug.log.length > 0 && (
          <SourcesPanel log={turn.response.debug.log} />
        )}
    </div>
  );
}

/** Returns ``true`` only when the URL carries ``?debug=tools`` (or
 *  the per-session localStorage flag set by the same handler). Lets
 *  Lenavio engineers inspect the planner's tool trail in prod
 *  without putting it in front of every user. */
function showSourcesDebug(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("debug") === "tools") {
      // Sticky — if you've ever opened debug mode in this session
      // it stays on as you navigate around the app.
      window.sessionStorage.setItem("lenavio.command.debug.tools", "1");
      return true;
    }
    return window.sessionStorage.getItem("lenavio.command.debug.tools") === "1";
  } catch {
    return false;
  }
}

function SourcesPanel({
  log,
}: {
  log: { name: string; params: unknown; elapsed_ms: number; ok: boolean }[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="text-xs text-text-muted px-1"
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer select-none hover:text-text-primary inline-flex items-center gap-1.5">
        <Search className="h-3 w-3" />
        <span>Sources · {log.length} tool{log.length === 1 ? "" : "s"}</span>
      </summary>
      <ul className="mt-2 space-y-1 pl-5">
        {log.map((row, i) => (
          <li key={i} className="font-mono">
            <span className={row.ok ? "text-success" : "text-warning"}>
              {row.ok ? "✓" : "⚠"}
            </span>{" "}
            <span className="text-text-primary">{row.name}</span>
            {row.params != null && Object.keys(row.params as object).length > 0 && (
              <span>({JSON.stringify(row.params)})</span>
            )}{" "}
            <span className="text-text-muted">— {row.elapsed_ms}ms</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * FileDownloadChip — prominent download affordance for a generated
 * file (PDF / XLSX / DOCX / PPTX) the planner produced via the
 * document Skills.
 *
 * Why a dedicated component:
 *   • The base64 → blob → anchor click dance was inlined as an
 *     arrow function; pulling it out makes the call site readable
 *     and lets us add per-extension iconography + size formatting
 *     without bloating the parent ResponseCard.
 *   • Filename extension drives the accent color so PDFs read red,
 *     spreadsheets green, slides orange — mirroring the conventions
 *     Office/macOS use, so the user recognises the type before they
 *     hover.
 *   • Errors surface via the parent's toast — we expose the catch
 *     via the ``onError`` prop instead of importing the toast hook
 *     here (one less coupling).
 */
function FileDownloadChip({
  file,
  onError,
}: {
  file: import("@/api/aiCommand").GeneratedFile;
  onError: (err: unknown) => void;
}) {
  const onClick = () => {
    try {
      const bin = atob(file.data_base64);
      const bytes = new Uint8Array(bin.length);
      for (let k = 0; k < bin.length; k++) bytes[k] = bin.charCodeAt(k);
      const blob = new Blob([bytes], { type: file.media_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Release the blob URL after a tick so the browser has time to
      // dispatch the download.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("file download failed", err);
      onError(err);
    }
  };

  const ext = (file.name.split(".").pop() || "").toLowerCase();
  const tone = fileTone(ext, file.media_type);
  const sizeLabel = formatFileSize(file.size);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group inline-flex items-center gap-3 rounded-xl border bg-surface px-5 py-3 text-left transition-all hover:shadow-sm active:scale-[0.98]",
        tone.border,
      )}
      aria-label={`Download ${file.name}`}
    >
      <span
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-semibold uppercase tracking-wide",
          tone.badge,
        )}
      >
        {ext.slice(0, 4) || "FILE"}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium text-text-primary">
          {file.name}
        </span>
        <span className="flex items-center gap-1.5 text-xs text-text-muted">
          <Download className="h-3 w-3" />
          <span>Download · {sizeLabel}</span>
        </span>
      </span>
    </button>
  );
}

/** Map an extension/MIME type to a tone so the icon + border read
 *  as the right kind of document at a glance. Defaults to a neutral
 *  border + grey badge so unknown types still render cleanly. */
function fileTone(ext: string, mediaType: string): {
  border: string;
  badge: string;
} {
  if (ext === "pdf" || mediaType === "application/pdf") {
    return {
      border: "border-rose-200/70 hover:border-rose-300",
      badge: "bg-rose-50 text-rose-700",
    };
  }
  if (
    ext === "xlsx" ||
    ext === "xls" ||
    ext === "csv" ||
    mediaType.includes("spreadsheet") ||
    mediaType === "text/csv"
  ) {
    return {
      border: "border-emerald-200/70 hover:border-emerald-300",
      badge: "bg-emerald-50 text-emerald-700",
    };
  }
  if (
    ext === "docx" ||
    ext === "doc" ||
    mediaType.includes("wordprocessing")
  ) {
    return {
      border: "border-sky-200/70 hover:border-sky-300",
      badge: "bg-sky-50 text-sky-700",
    };
  }
  if (
    ext === "pptx" ||
    ext === "ppt" ||
    mediaType.includes("presentation")
  ) {
    return {
      border: "border-amber-200/70 hover:border-amber-300",
      badge: "bg-amber-50 text-amber-700",
    };
  }
  return {
    border: "border-border hover:border-accent/50",
    badge: "bg-surface-muted text-text-muted",
  };
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
}

/** Rotating "what the AI is doing right now" status messages. The
 *  agent loop on the backend doesn't actually stream these — we
 *  cycle through plausible phases so the user perceives genuine
 *  activity rather than a static spinner. The phases match the
 *  real tool-use lifecycle (search → look up → reason → answer)
 *  so it never feels misleading. */
const THINKING_PHASES = [
  "Reading your question…",
  "Searching your data…",
  "Pulling the right context…",
  "Working through it…",
  "Putting it together…",
];

/**
 * ProgressTrail — live readout of what the engine is doing.
 *
 * Each NDJSON event from ``/ai/command/stream`` becomes a line in
 * the trail: "Planning…", per-tool starts/finishes with elapsed
 * time, and a "Building report…" line just before the final
 * payload arrives. When the loop is fast enough that the trail
 * scrolls by in <1 second the user just sees a polished spinner;
 * on slow runs they see exactly which tools fired in what order.
 */
function ProgressTrail({ events }: { events: CommandStreamEvent[] }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-xl border border-accent/20 bg-gradient-to-r from-accent/5 via-accent/10 to-accent/5 px-4 py-3"
    >
      <motion.div
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent"
        animate={{ x: ["-100%", "100%"] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
      />
      <div className="flex items-start gap-3">
        <motion.span
          animate={{ scale: [1, 1.2, 1], rotate: [0, 12, -12, 0], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 1.2, repeat: Infinity }}
          className="text-accent inline-flex mt-0.5"
        >
          <Sparkles className="h-5 w-5" />
        </motion.span>
        <ul className="flex-1 min-w-0 space-y-0.5 text-xs">
          {events.slice(-6).map((ev, i) => (
            <li key={i} className="flex items-center gap-2">
              <span
                className={
                  ev.kind === "tool_done" && !ev.ok
                    ? "text-warning"
                    : "text-text-muted"
                }
              >
                {trailLabel(ev)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

function trailLabel(ev: CommandStreamEvent): string {
  switch (ev.kind) {
    case "planning":
      return ev.message;
    case "tool_start":
      return `· Calling ${ev.tool}…`;
    case "tool_done":
      return `${ev.ok ? "✓" : "⚠"} ${ev.tool} (${ev.elapsed_ms}ms)`;
    case "synth":
      return ev.message;
    case "final":
      return "✓ Done";
    case "error":
      return `⚠ ${ev.message}`;
    default:
      // Exhaustive switch over the discriminated union — but TS can't
      // prove that at the function boundary without an explicit
      // default arm, so the build fails with TS2366. Returning ""
      // gives the function a provable ``string`` return type and
      // unblocks the Cloudflare Pages build that's been failing on
      // this for hours. Any future ``CommandStreamEvent`` variant we
      // add will silently fall through here and render as empty
      // string — acceptable as the trail row is a status indicator,
      // not a critical UI surface.
      return "";
  }
}

function ThinkingCard() {
  const [phaseIdx, setPhaseIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setPhaseIdx((i) => (i + 1) % THINKING_PHASES.length);
    }, 1400);
    return () => clearInterval(t);
  }, []);
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-xl border border-accent/20 bg-gradient-to-r from-accent/5 via-accent/10 to-accent/5 px-4 py-3"
    >
      {/* Shimmering accent bar across the top to sell "active". */}
      <motion.div
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent"
        animate={{ x: ["-100%", "100%"] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
      />
      <div className="flex items-center gap-3">
        <motion.span
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 12, -12, 0],
            opacity: [0.7, 1, 0.7],
          }}
          transition={{ duration: 1.2, repeat: Infinity }}
          className="text-accent inline-flex"
        >
          <Sparkles className="h-5 w-5" />
        </motion.span>
        <div className="flex-1 min-w-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={phaseIdx}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
              className="text-sm text-text-primary font-medium"
            >
              {THINKING_PHASES[phaseIdx]}
            </motion.div>
          </AnimatePresence>
          <div className="text-[11px] text-text-muted mt-0.5">
            Lenavio is reading your data + thinking
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * StreamingDraftCard — renders a drafting response as it streams in.
 * The accumulating text "types out" with a blinking caret so a long
 * generation (PIP, policy, memo) is visibly progressing instead of
 * sitting blank until the final payload lands. Rendered as plain
 * whitespace-pre-wrap during the stream (partial markdown can look
 * broken mid-token); the final ResponseCard re-renders it as markdown.
 */
function StreamingDraftCard({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  // Keep the newest text in view as the draft grows.
  useEffect(() => {
    ref.current?.scrollTo({ top: ref.current.scrollHeight });
  }, [text]);
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-xl border border-accent/20 bg-surface px-4 py-3"
    >
      <motion.div
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent"
        animate={{ x: ["-100%", "100%"] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
      />
      <div className="mb-2 flex items-center gap-2 text-xs font-medium text-accent">
        <motion.span
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.2, repeat: Infinity }}
          className="inline-flex"
        >
          <Sparkles className="h-4 w-4" />
        </motion.span>
        Drafting…
      </div>
      <div
        ref={ref}
        className="max-h-[420px] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-text-primary"
      >
        {text}
        <span className="ml-0.5 inline-block h-4 w-1.5 -mb-0.5 animate-pulse bg-accent/70 align-baseline" />
      </div>
    </motion.div>
  );
}

interface ResponseCardProps {
  response: CommandResponse;
  /** True when this card is the active confirmation handshake — the
   *  Confirm/Cancel buttons render under the narrative card and the
   *  user re-submits with confirmed=true on click. Per-turn boolean
   *  rather than the older PendingAction object: the parent owns
   *  the turn id, the card just needs to know whether to draw the
   *  buttons. */
  confirming: boolean;
  onConfirm: () => void;
  onCancelConfirm: () => void;
  onSuggestion: (s: string) => void;
}

function ResponseCard({
  response,
  confirming,
  onConfirm,
  onCancelConfirm,
  onSuggestion,
}: ResponseCardProps) {
  const { addToast } = useToast();
  const tone = toneFor(response.type);
  // The new V2 engine returns a ``sections`` array with stat cards,
  // charts, tables, etc. When present, we render the structured
  // report ABOVE the narrative text and skip the legacy single
  // bar-chart visualization (sections supersede it).
  const hasSections =
    Array.isArray(response.sections) && response.sections.length > 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3"
    >
      {/* Narrative summary card. Skinnier when there's a full
          sectioned report below it — the report carries the weight,
          this is just the headline. The text is run through
          MarkdownText so headings/bold/bullets in the planner's
          output get styled instead of leaking raw "## Summary" /
          "**bold**" characters into the UI. Inline ``[N]`` citation
          tokens are still tokenized to footnote pills. */}
      <div
        className={`rounded-lg border ${tone.borderClass} ${tone.bgClass} px-4 py-3`}
      >
        <div className="flex items-start gap-2">
          <span className={`shrink-0 mt-0.5 ${tone.iconClass}`}>
            {tone.icon}
          </span>
          <div className="flex-1 min-w-0">
            <MarkdownText
              text={response.text}
              citations={response.citations}
            />
          </div>
        </div>

        {/* Action confirmation lives inside the narrative card so the
            Confirm/Cancel buttons sit right under the description. */}
        {confirming && response.requires_confirmation && (
          <div className="mt-3 border-t border-border/40 pt-3">
            {/* Legacy executors sometimes return a per-row preview;
                show it under the prompt. */}
            {Array.isArray(
              (response.data as { requests?: unknown[] })?.requests,
            ) && (
              <ul className="text-xs text-text-muted space-y-1 mb-3 max-h-40 overflow-y-auto">
                {(
                  (response.data as {
                    requests: {
                      employee: string;
                      type: string;
                      start: string;
                      end: string;
                    }[];
                  }).requests
                ).map((r, i) => (
                  <li key={i}>
                    · {r.employee} — {r.type.replace(/_/g, " ")} ({r.start} → {r.end})
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={onCancelConfirm}>
                Cancel
              </Button>
              <Button size="sm" onClick={onConfirm}>
                <Check className="h-4 w-4" />
                Confirm
              </Button>
            </div>
          </div>
        )}

        {/* Credit-cost confirmation — fires on 15+ credit commands.
            Frames the UI redirect as a legitimate alternative, NOT
            a punishment. Three buttons:
              * Cancel       — close the card, no action
              * Go to {path} — link to the UI surface that does the
                same job WITHOUT credits (the whole point of the
                reframe: command bar is a shortcut, not a gate)
              * Confirm — re-issue the prompt with confirmed=true
                so the engine actually runs (and bills) it.
        */}
        {confirming && response.requires_credit_confirmation && (
          <div className="mt-3 border-t border-border/40 pt-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs text-text-muted">
                Skip the credit charge by using the regular interface —
                it works the same.
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onCancelConfirm}
                >
                  Cancel
                </Button>
                {response.redirect_path && (
                  <CreditRedirectButton
                    path={response.redirect_path}
                    onClick={onCancelConfirm}
                  />
                )}
                <Button size="sm" onClick={onConfirm}>
                  <Check className="h-4 w-4" />
                  Confirm — {response.credits_needed ?? 0} credits
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Legacy single-bar viz — only renders when the V2
            sections array is empty. The new engine provides
            ``sections`` with rich stats/charts/tables; if those
            are present the renderer below takes over. */}
        {!hasSections &&
          response.type === "report" &&
          response.visualization?.series && (
            <div className="mt-3">
              <BarChart
                series={response.visualization.series}
                chart={response.visualization.chart}
              />
            </div>
          )}
      </div>

      {/* Phase 8: matplotlib / code-execution images. Render above
          the structured sections — usually the image IS the report
          and the bento cards are supplementary. */}
      {response.images && response.images.length > 0 && (
        <div className="space-y-3">
          {response.images.map((img, i) => (
            <div
              key={i}
              className="overflow-hidden rounded-2xl border border-border/40 bg-surface"
            >
              <img
                src={`data:${img.media_type};base64,${img.data}`}
                alt="Generated chart"
                className="block w-full"
              />
            </div>
          ))}
        </div>
      )}

      {/* Phase 8: file downloads (xlsx / pdf / docx / pptx) from
          the document skills. Base64 inline → blob URL → download.
          Rendered as prominent action chips so the user immediately
          sees "there's a deliverable here" — the document is often
          THE answer (a generated report), not a side note. */}
      {response.files && response.files.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {response.files.map((f, i) => (
            <FileDownloadChip
              key={i}
              file={f}
              onError={() =>
                addToast("error", "Could not download file")
              }
            />
          ))}
        </div>
      )}

      {/* V2 structured report — stats / bar / pie / line / table /
          text. Cards stack below the narrative summary. */}
      {hasSections && (
        <ReportRenderer
          sections={response.sections!}
          actions={response.actions}
          suggestions={response.suggestions}
          citations={response.citations}
          onAction={async (label) => {
            // The last command the user submitted lives at the top
            // of the recents array (push happens on success). Use
            // it as the prompt for any "Schedule …" handoff.
            const lastCmd = (readHistory()[0] || "").trim();
            const lower = label.toLowerCase();
            // Schedule weekly / monthly / daily — call the
            // recurring-runner endpoint and report back.
            if (lower.includes("schedule")) {
              if (!lastCmd) {
                addToast("error", "No recent command to schedule.");
                return;
              }
              const cadence: "weekly" | "monthly" | "daily" | "once" =
                lower.includes("daily")
                  ? "daily"
                  : lower.includes("monthly")
                    ? "monthly"
                    : lower.includes("once")
                      ? "once"
                      : "weekly";
              try {
                const r = await scheduleCommand({
                  command: lastCmd,
                  cadence,
                  label: lastCmd.slice(0, 80),
                  delivery: "email",
                });
                addToast(
                  "success",
                  `Scheduled ${cadence} — first run ${new Date(r.next_run_at).toLocaleString()}. You'll get the report by email.`,
                );
              } catch (err) {
                console.error("scheduleCommand failed", err);
                addToast("error", "Couldn't schedule that command.");
              }
              return;
            }
            // Download PDF / Export — generate a PDF on the fly from
            // ``response.sections + response.text``. When the planner
            // already produced a real PDF via Skills (e.g. a server-
            // side document render), ``exportReportToPdf`` downloads
            // that verbatim instead of re-rendering — higher fidelity
            // than anything we'd reproduce client-side.
            //
            // Lazy-load: jspdf + html2canvas are ~430KB of JS. Only
            // pay that cost the first time the user clicks Download
            // in a session; the browser caches the chunk afterwards.
            const looksLikeDownload =
              (lower.includes("download") ||
                lower.includes("export") ||
                lower.includes("save")) &&
              (lower.includes("pdf") ||
                lower.includes("report") ||
                lower.includes("file"));
            if (looksLikeDownload) {
              try {
                const { exportReportToPdf } = await import(
                  "@/lib/reportPdf"
                );
                // Now async: charts in the report get rasterised
                // server-side via /ai/charts/render before the
                // jsPDF draw pass runs. Adds one HTTP round-trip
                // per chart (parallelised) — typically <500ms total.
                const filename = await exportReportToPdf(response);
                addToast("success", `Downloaded ${filename}`);
              } catch (err) {
                console.error("exportReportToPdf failed", err);
                addToast(
                  "error",
                  "Couldn't generate the PDF. Try again or copy the data manually.",
                );
              }
              return;
            }
            // Email to team — needs a backend pipeline (build the
            // PDF + send via the email service). Until that lands,
            // fall back to a copy-to-clipboard hint so the user
            // still has a path forward.
            const isEmail = /\bemail\b/i.test(label);
            if (isEmail) {
              try {
                const text = (response.text || "").trim() ||
                  "Lenavio report";
                if (navigator.clipboard?.writeText) {
                  await navigator.clipboard.writeText(text);
                  addToast(
                    "info",
                    "Copied the summary to your clipboard — paste it into an email.",
                  );
                } else {
                  addToast(
                    "info",
                    "Email sending isn't wired yet. Use Download PDF and attach it.",
                  );
                }
              } catch {
                addToast(
                  "info",
                  "Email sending isn't wired yet. Use Download PDF and attach it.",
                );
              }
              return;
            }
            // Any other action label the planner invents — surface a
            // gentle message rather than silently doing nothing.
            addToast(
              "info",
              `"${label}" isn't wired to a handler yet. Let support know if you'd like it.`,
            );
          }}
          onSuggestion={onSuggestion}
        />
      )}

      {/* Legacy suggestions — only render when V2 sections didn't
          already include them (the renderer above already shows
          ``response.suggestions`` when sections are present). */}
      {!hasSections &&
        response.suggestions &&
        response.suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-1">
            {response.suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onSuggestion(s)}
                className="text-xs px-2.5 py-1 rounded-full border border-border/60 bg-surface/60 text-text-primary hover:bg-surface-hover hover:border-accent/40 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}

      {/* Credit telemetry. For credit-denied responses we render a
          prominent CTA pointing at Settings → Billing; for normal
          responses we surface the "N credits used · M remaining"
          footnote so admins can keep an eye on burn rate without
          leaving the command bar. ``credits_remaining`` is null on
          internal orgs (we never gate them) and on legacy responses
          from before the credit system shipped. */}
      <CreditsTelemetry response={response} />
    </motion.div>
  );
}

/**
 * "Go to {path}" button used inside the credit-confirmation card.
 *
 * The redirect target is a human-readable breadcrumb ("People → Add
 * Employee"), not a URL — we map the most common breadcrumbs to
 * known routes so the click actually goes somewhere. Anything we
 * don't recognise falls back to opening a small toast that tells
 * the user where to find the screen (an HR admin who's seeing
 * "Reports → Compliance" will know what page that is even without
 * a deep link).
 *
 * ``onClick`` is the parent's ``onCancelConfirm`` — clicking the
 * redirect closes the confirmation card so it doesn't keep sitting
 * there after the user navigates.
 */
function CreditRedirectButton({
  path,
  onClick,
}: {
  path: string;
  onClick: () => void;
}) {
  const navigate = useNavigate();
  // Conservative mapping — only routes we know exist. Falls back
  // to /dashboard when the breadcrumb starts with something we
  // haven't wired (still better than 404'ing the user).
  const route = redirectPathToRoute(path);
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => {
        onClick();
        navigate(route);
      }}
    >
      <ArrowRight className="h-4 w-4" />
      Go to {path.split(" → ")[0] || "module"}
    </Button>
  );
}

/** Map the human-readable breadcrumb the backend returns to a real
 *  SPA route. We match on the FIRST segment of the breadcrumb so
 *  "People → Add Employee" and "People → [Employee] → Offboard"
 *  both land on the People list (the user can drill in from there).
 *  Anything unrecognized lands on the dashboard with the breadcrumb
 *  visible — never 404. */
function redirectPathToRoute(path: string): string {
  const first = path.split(" → ")[0]?.trim().toLowerCase();
  switch (first) {
    case "people":
      return "/people";
    case "reports":
      return "/reports";
    case "benefits":
      return "/benefits";
    case "leave":
      return "/leave";
    case "documents":
      return "/documents";
    case "directory":
      return "/directory";
    case "settings":
      return "/settings";
    default:
      return "/dashboard";
  }
}


/**
 * Inline credits footer / denial CTA for the command bar response.
 *
 * Three render paths:
 *   * ``show_upgrade`` set → credit-exhausted denial. Renders the
 *     reframed message + a "Go to {ui_path}" link (always — the
 *     point of the reframe is that the UI works without credits)
 *     and a "Buy more credits" CTA (SUPER_ADMIN only).
 *   * ``credits_remaining`` non-null → normal response. Renders a
 *     muted "N credits used · M remaining" footnote, plus a
 *     reassurance line when balance is below 20%.
 *   * Otherwise → render nothing. Covers internal orgs and legacy
 *     responses that predate the credit field.
 */
function CreditsTelemetry({ response }: { response: CommandResponse }) {
  // ``useAuth`` is already imported at the top of this file.
  // We re-use it here to gate the upgrade chip to roles who can
  // actually act on it.
  const { user } = useAuth();
  const role = user?.role ?? "EMPLOYEE";
  const canPurchase = role === "SUPER_ADMIN";

  // ── Credit-exhausted denial ───────────────────────────────────
  // The engine returns the reframed "you can still {verb} from
  // {path}" message in ``response.text`` already. We surface the
  // UI redirect as a real button (not just text) so the user can
  // act in one click. "Buy more credits" goes to SUPER_ADMIN only.
  if (response.show_upgrade) {
    const route = response.redirect_path
      ? redirectPathToRoute(response.redirect_path)
      : null;
    return (
      <div className="flex flex-wrap items-center gap-2 px-1 text-xs">
        {response.redirect_path && route && (
          <Link
            to={route}
            className="rounded-full border border-border bg-surface px-3 py-1 font-medium text-text-primary hover:border-accent/40 hover:text-accent"
          >
            Go to {response.redirect_path.split(" → ")[0]}
          </Link>
        )}
        {canPurchase ? (
          <Link
            to="/settings/billing"
            className="rounded-full bg-warning/10 px-3 py-1 font-semibold text-warning hover:bg-warning/20"
          >
            Buy more credits
          </Link>
        ) : (
          <span className="text-text-muted">
            Ask a SUPER_ADMIN to buy more.
          </span>
        )}
      </div>
    );
  }

  if (
    response.credits_remaining == null ||
    response.credits_used == null
  ) {
    return null;
  }

  // ── Normal response footer ────────────────────────────────────
  // Always show "N credits used · M remaining". When balance dips
  // below the reassurance threshold (20% of typical monthly cap),
  // append the spec's line:
  //   "All features still work from the regular interface"
  // — never imply that low credits = missing features.
  //
  // We don't have the monthly_allowance on the per-response payload
  // (it's on the /billing/credits endpoint, fetched separately by
  // the LowCreditBanner). For an inline reassurance we use a flat
  // 50-credit threshold as the trigger — comfortably above the
  // cheapest action (1 credit) and below the cost of one Tier-3
  // command (15-25 credits), so any admin who's at risk of running
  // out within a few commands sees the line.
  const LOW_BALANCE_HINT = 50;
  const isLow = response.credits_remaining <= LOW_BALANCE_HINT;

  return (
    <div className="px-1 text-[11px] text-text-muted">
      <span className="tabular-nums">{response.credits_used}</span>{" "}
      credit{response.credits_used === 1 ? "" : "s"} used ·{" "}
      <span
        className={isLow ? "tabular-nums font-medium text-warning" : "tabular-nums"}
      >
        {response.credits_remaining}
      </span>{" "}
      remaining
      {isLow && (
        <span className="text-text-muted">
          {" · "}All features still work from the regular interface
        </span>
      )}
    </div>
  );
}

function toneFor(type: CommandResponse["type"]): {
  icon: React.ReactNode;
  iconClass: string;
  borderClass: string;
  bgClass: string;
} {
  switch (type) {
    case "denied":
      return {
        icon: <ShieldOff className="h-4 w-4" />,
        iconClass: "text-warning",
        borderClass: "border-warning/30",
        bgClass: "bg-warning/5",
      };
    case "error":
      return {
        icon: <TriangleAlert className="h-4 w-4" />,
        iconClass: "text-danger",
        borderClass: "border-danger/30",
        bgClass: "bg-danger/5",
      };
    case "unknown":
      return {
        icon: <Search className="h-4 w-4" />,
        iconClass: "text-text-muted",
        borderClass: "border-border/40",
        bgClass: "bg-surface/60",
      };
    case "report":
      return {
        icon: <ClipboardList className="h-4 w-4" />,
        iconClass: "text-accent",
        borderClass: "border-accent/30",
        bgClass: "bg-accent/5",
      };
    case "navigate":
      return {
        icon: <ArrowRight className="h-4 w-4" />,
        iconClass: "text-accent",
        borderClass: "border-accent/30",
        bgClass: "bg-accent/5",
      };
    default:
      return {
        icon: <Sparkles className="h-4 w-4" />,
        iconClass: "text-accent",
        borderClass: "border-border/40",
        bgClass: "bg-surface/60",
      };
  }
}

function BarChart({
  series,
  chart,
}: {
  series: { label: string; value: number }[];
  chart: string;
}) {
  if (series.length === 0) return null;
  const max = Math.max(1, ...series.map((s) => s.value));
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-text-muted mb-2">
        {chart === "bar" ? "Bar chart" : chart}
      </div>
      <ul className="space-y-1.5">
        {series.map((s) => (
          <li key={s.label} className="flex items-center gap-2">
            <span className="w-32 text-xs text-text-muted truncate">
              {s.label}
            </span>
            <div className="flex-1 h-3 rounded bg-surface-hover/60 overflow-hidden">
              <div
                className="h-full bg-accent/70 rounded"
                style={{ width: `${(s.value / max) * 100}%` }}
              />
            </div>
            <span className="w-10 text-xs text-text-primary text-right tabular-nums">
              {s.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default CommandBar;
